import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import CustomCircularProgress from '../customCircularProgress/customCircularProgress.component';
import { useCRUD } from '../../../api/crud';
import {
  JOB_STATUS_TRANSITION_ACTIONS_ENDPOINT,
  JOB_STATUS_OPTIONS,
  JOB_NEXT_STATUSES_MAP,
  JOB_KIND_OPTIONS,
  SUCCESS_SNACKBAR,
  ERROR_SNACKBAR,
} from '../../../consts';
import { useAuthContext } from '../../../contexts/auth';

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

type RequiredFieldType = 'V' | 'T' | 'S' | 'I' | 'U' | 'R';

const FIELD_TYPE_OPTIONS: { value: RequiredFieldType; labelKey: string }[] = [
  { value: 'V', labelKey: 'Counter values' },
  { value: 'T', labelKey: 'Description' },
  { value: 'S', labelKey: 'Services' },
  { value: 'I', labelKey: 'Issues' },
  { value: 'U', labelKey: 'Used parts' },
  { value: 'R', labelKey: 'Rejection reason' },
];

type Props = {
  id?: string | number;
  onSuccess: () => void;
};

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

const extractError = (errors: Record<string, unknown>, key: string): string => {
  const val = errors[key];
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (Array.isArray(val) && val.length > 0) {
    const first = val[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object' && 'message' in first)
      return (first as { message?: string }).message ?? '';
  }
  return '';
};

const collectAllErrors = (errorData: Record<string, unknown>): string => {
  const messages: string[] = [];
  for (const key of Object.keys(errorData)) {
    const msg = extractError(errorData, key);
    if (msg) messages.push(msg);
  }
  return messages.join(' ');
};

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

const StatusTransitionActionForm: React.FC<Props> = ({ id, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuthContext();
  const { loading, get, create, updatePUT } = useCRUD(JOB_STATUS_TRANSITION_ACTIONS_ENDPOINT);

  const [initialLoading, setInitialLoading] = useState(!!id);
  const [previousStatus, setPreviousStatus] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [obligatoryTypes, setObligatoryTypes] = useState<RequiredFieldType[]>([]);
  const [optionalTypes, setOptionalTypes] = useState<RequiredFieldType[]>([]);
  const [jobKinds, setJobKinds] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, unknown>>({});
  const canAddAnyJob = Boolean(currentUser?.permissions?.includes('service.add_job'));
  const canAddInternalJobOnly = Boolean(
    currentUser?.permissions?.includes('service.add_internal_job') && !canAddAnyJob,
  );
  const availableJobKindOptions = canAddInternalJobOnly
    ? JOB_KIND_OPTIONS.filter((option) => option.value === 'I')
    : JOB_KIND_OPTIONS;

  useEffect(() => {
    if (!id) return;
    get(id).then((result) => {
      if (result?.success && result.data) {
        const d = result.data;
        setPreviousStatus(d.previous_status ?? '');
        setNewStatus(d.new_status ?? '');
        const fields = (d.required_fields ?? []) as Array<{ field_type: RequiredFieldType; is_required?: boolean }>;
        setObligatoryTypes(Array.from(new Set(fields.filter((rf) => rf.is_required ?? true).map((rf) => rf.field_type))));
        setOptionalTypes(Array.from(new Set(fields.filter((rf) => !(rf.is_required ?? true)).map((rf) => rf.field_type))));
        setJobKinds(new Set((d.job_kinds ?? []).map((jk: any) => jk.kind)));
      }
      setInitialLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!previousStatus) return;
    const allowedNextStatuses = JOB_NEXT_STATUSES_MAP[previousStatus] ?? [];
    if (allowedNextStatuses.length === 1) {
      setNewStatus(allowedNextStatuses[0]);
    }
  }, [previousStatus]);

  const handleObligatoryChange = (event: SelectChangeEvent<RequiredFieldType[]>) => {
    const value = event.target.value as RequiredFieldType[];
    setObligatoryTypes(value);
    setOptionalTypes((prev) => prev.filter((type) => !value.includes(type)));
  };

  const handleOptionalChange = (event: SelectChangeEvent<RequiredFieldType[]>) => {
    const value = event.target.value as RequiredFieldType[];
    setOptionalTypes(value);
    setObligatoryTypes((prev) => prev.filter((type) => !value.includes(type)));
  };

  const toggleKind = (kind: string) =>
    setJobKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });

  const handleSubmit = async () => {
    setErrors({});
    const payload = {
      previous_status: previousStatus,
      new_status: newStatus,
      required_fields: [
        ...obligatoryTypes.map((field_type) => ({ field_type, checkbox_label: null, is_required: true })),
        ...optionalTypes.map((field_type) => ({ field_type, checkbox_label: null, is_required: false })),
      ],
      job_kinds: Array.from(jobKinds).map((kind) => ({ kind })),
    };

    let result;
    if (id) {
      result = await updatePUT({ id, ...payload });
    } else {
      result = await create(payload);
    }

    if (result?.success) {
      enqueueSnackbar(
        t(id ? 'Status transition action saved successfully' : 'Status transition action created successfully'),
        SUCCESS_SNACKBAR,
      );
      onSuccess();
    } else if (result?.errorData) {
      setErrors(result.errorData);
      const message = collectAllErrors(result.errorData) || t('Please fix the errors and try again.');
      enqueueSnackbar(message, ERROR_SNACKBAR);
    } else {
      enqueueSnackbar(t('An unexpected error occurred'), ERROR_SNACKBAR);
    }
  };

  if (initialLoading) return <CustomCircularProgress />;

  const topLevelError =
    extractError(errors, 'non_field_errors') || extractError(errors, 'detail');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
      {topLevelError && <Alert severity="error">{topLevelError}</Alert>}

      {/* Status selects */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <FormControl fullWidth error={!!extractError(errors, 'previous_status')}>
          <InputLabel>{t('Previous status')}</InputLabel>
          <Select
            value={previousStatus}
            label={t('Previous status')}
            onChange={(e) => {
              setPreviousStatus(e.target.value);
              setNewStatus('');
            }}
          >
            {JOB_STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {t(o.labelKey)}
              </MenuItem>
            ))}
          </Select>
          {extractError(errors, 'previous_status') && (
            <FormHelperText>{extractError(errors, 'previous_status')}</FormHelperText>
          )}
        </FormControl>

        <FormControl fullWidth error={!!extractError(errors, 'new_status')}>
          <InputLabel>{t('New status')}</InputLabel>
          <Select
            value={newStatus}
            label={t('New status')}
            onChange={(e) => setNewStatus(e.target.value)}
            disabled={!previousStatus}
          >
            {(previousStatus ? JOB_NEXT_STATUSES_MAP[previousStatus] ?? [] : []).map((val) => {
              const option = JOB_STATUS_OPTIONS.find((o) => o.value === val);
              return option ? (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </MenuItem>
              ) : null;
            })}
          </Select>
          {extractError(errors, 'new_status') && (
            <FormHelperText>{extractError(errors, 'new_status')}</FormHelperText>
          )}
        </FormControl>
      </Box>

      <Divider />

      {/* Required fields */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('Required fields')}
        </Typography>

        <FormControl fullWidth>
          <InputLabel>{t('Obligatory fields')}</InputLabel>
          <Select
            multiple
            label={t('Obligatory fields')}
            value={obligatoryTypes}
            onChange={handleObligatoryChange}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as RequiredFieldType[]).map((value) => (
                  <Chip
                    key={value}
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={t(FIELD_TYPE_OPTIONS.find((o) => o.value === value)?.labelKey ?? value)}
                  />
                ))}
              </Box>
            )}
          >
            {FIELD_TYPE_OPTIONS.filter((o) => !optionalTypes.includes(o.value)).map((o) => (
              <MenuItem key={o.value} value={o.value}>
                <Checkbox checked={obligatoryTypes.includes(o.value)} />
                <ListItemText primary={t(o.labelKey)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>{t('Optional fields')}</InputLabel>
          <Select
            multiple
            label={t('Optional fields')}
            value={optionalTypes}
            onChange={handleOptionalChange}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as RequiredFieldType[]).map((value) => (
                  <Chip
                    key={value}
                    size="small"
                    variant="outlined"
                    label={t(FIELD_TYPE_OPTIONS.find((o) => o.value === value)?.labelKey ?? value)}
                  />
                ))}
              </Box>
            )}
          >
            {FIELD_TYPE_OPTIONS.filter((o) => !obligatoryTypes.includes(o.value)).map((o) => (
              <MenuItem key={o.value} value={o.value}>
                <Checkbox checked={optionalTypes.includes(o.value)} />
                <ListItemText primary={t(o.labelKey)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Divider />

      {/* Job kinds */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('Job kinds')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {availableJobKindOptions.map((o) => (
            <FormControlLabel
              key={o.value}
              control={
                <Checkbox
                  checked={jobKinds.has(o.value)}
                  onChange={() => toggleKind(o.value)}
                />
              }
              label={t(o.labelKey)}
            />
          ))}
        </Box>
      </Box>

      <Button variant="contained" onClick={handleSubmit} disabled={loading}>
        {loading ? t('Processing...') : t('Submit')}
      </Button>
    </Box>
  );
};

export default StatusTransitionActionForm;
